import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SubscriptionPaymentModal from '../components/SubscriptionPaymentModal';
import { landingContent, testimonials } from '../components/landing-page/content';
import LiveChat from '../components/landing-page/LiveChat';
import DemoFormModal from '../components/landing-page/DemoFormModal';
import PricingCalculatorModal from '../components/landing-page/PricingCalculatorModal';
import { usePricingConfig } from '../hooks/usePricingConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Day number → feature slug mapping for deep linking
const DAY_TO_SLUG = {
  1: 'generate-mcq', 2: 'simplify-notice', 3: 'fee-reminder', 4: 'attendance-alerts',
  5: 'question-paper-gen', 6: 'progress-summary', 7: 'compliance-checklist', 8: 'homework-hint',
  9: 'chapter-explanation', 10: 'study-plan', 11: 'faq', 12: 'report-card-gen',
  13: 'class-weak-topics', 14: 'at-risk-alerts', 15: 'daily-summary', 16: 'complaint-draft',
  17: 'timetable-generator', 18: 'leave-management', 19: 'transport-management', 20: 'emergency-exit',
  21: 'online-fee-payment', 22: 'practice-tests', 23: 'bulk-import', 24: 'whatsapp-broadcast',
  25: 'udise-portal', 26: 'social-media-manager', 27: 'school-profile', 28: 'child-progress',
  29: 'analytics-dashboard', 30: 'ai-wallet', 31: 'complete-package'
};

// Use imported content
const content = landingContent;

export default function LandingPage({ onLoginClick, setCurrentPage }) {
  const navigate = useNavigate();
  const [language, setLanguage] = useState('hi');
  const [showDemoForm, setShowDemoForm] = useState(false);
  const [selectedSize, setSelectedSize] = useState('small');
  const [formData, setFormData] = useState({ name: '', school: '', phone: '', email: '', students: '', message: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [allFeatures, setAllFeatures] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [featureDetail, setFeatureDetail] = useState(null);
  const [featureDetailLoading, setFeatureDetailLoading] = useState(false);
  const [featuresLoading, setFeaturesLoading] = useState(true);
  const [featuresError, setFeaturesError] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [featureContextForChat, setFeatureContextForChat] = useState(null);
  // New states for pricing enhancements
  const [userCount, setUserCount] = useState(50);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [activeFeatureTab, setActiveFeatureTab] = useState('core');
  const [activeComparisonTab, setActiveComparisonTab] = useState('core');
  const [showPricingCalculator, setShowPricingCalculator] = useState(false);
  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [demoModalPrefill, setDemoModalPrefill] = useState(null);
  const [paymentModalPrefill, setPaymentModalPrefill] = useState(null);
  const [paymentModalStartStep, setPaymentModalStartStep] = useState(1);
  // Real founding schools counter (from backend) — defaults are re-sync'd from pricingCfg after it loads.
  const [foundingStats, setFoundingStats] = useState({ claimed: 0, remaining: 50, total_slots: 50, percent_filled: 0, is_open: true });
  const [calcStudents, setCalcStudents] = useState('');

  // Centralized pricing config (single source of truth)
  const { config: pricingCfg } = usePricingConfig();
  const BASE_PRICE = pricingCfg.base_price_per_user_year;
  const FOUNDING_PRICE = pricingCfg.founding_price_per_user_year;
  const ONE_TIME = pricingCfg.one_time_price;
  const FOUNDING_ONE = pricingCfg.founding_one_time_price;
  const MAINT = pricingCfg.annual_maintenance;
  const FOUNDING_PCT = pricingCfg.founding_discount_percent;
  const FOUNDING_LIMIT = pricingCfg.founding_schools_limit;
  const CURR = pricingCfg.currency_symbol;

  // Slab/tier calculator (student-count based)
  const calcTiers = pricingCfg.pricing_tiers || [];
  const calcCount = parseInt(calcStudents, 10) || 0;
  const calcTier = calcCount > 0 ? calcTiers.find((t) => calcCount >= t.min && (t.max == null || calcCount <= t.max)) : null;
  const calcCustom = !!calcTier?.custom;
  const calcOneTime = calcTier && !calcCustom ? calcTier.one_time : 0;
  const calcMaint = calcTier && !calcCustom ? calcTier.annual_maintenance : 0;
  const calcTotal = calcOneTime + calcMaint;
  const SALES_PHONE = pricingCfg.sales_contact_phone || '9826618603';

  useEffect(() => {
    // Fetch real founding schools count from backend
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/platform-admin/founding-schools/stats`)
      .then(r => r.json())
      .then(d => { if (d?.success) setFoundingStats(d); })
      .catch(e => console.warn('founding stats load failed:', e));
  }, []);
  const [selectedPlanForPayment, setSelectedPlanForPayment] = useState(null);
  const [brandContent, setBrandContent] = useState(null);
  const t = content[language];
  const aboutCards = [
    {
      title: language === 'hi' ? 'About Us' : 'About Us',
      text: language === 'hi'
        ? 'शिक्षा मित्र schools ke liye ek AI-first growth aur operations platform है जो admission, ERP, communication aur payments ko एक जगह लाता है।'
        : 'Shiksha Mitra is an AI-first growth and operations platform for schools that brings admissions, ERP, communication, and payments into one place.',
      icon: '🏫'
    },
    {
      title: language === 'hi' ? 'Vision' : 'Vision',
      text: language === 'hi'
        ? 'हर Indian school ko ek ऐसा unified operating system देना jo principal, staff aur parents sabko clarity दे।'
        : 'To give every Indian school a unified operating system that brings clarity to principals, staff, and parents.',
      icon: '🌟'
    },
    {
      title: language === 'hi' ? 'Mission' : 'Mission',
      text: language === 'hi'
        ? 'Manual stress ko कम करना, teams ko तेज़ बनाना, aur schools ko measurable growth-ready workflows देना।'
        : 'Reduce manual stress, make teams faster, and give schools measurable growth-ready workflows.',
      icon: '🎯'
    },
  ];
  const teamPlaceholders = [
    {
      role: language === 'hi' ? 'Founder / Leadership' : 'Founder / Leadership',
      summary: language === 'hi' ? 'Strategy, education vision aur category direction' : 'Strategy, education vision, and category direction'
    },
    {
      role: language === 'hi' ? 'Product & Engineering' : 'Product & Engineering',
      summary: language === 'hi' ? 'ERP, AI, automation aur core platform systems' : 'ERP, AI, automation, and core platform systems'
    },
    {
      role: language === 'hi' ? 'Growth & Outreach' : 'Growth & Outreach',
      summary: language === 'hi' ? 'School acquisition, CRM, campaigns aur conversion' : 'School acquisition, CRM, campaigns, and conversion'
    },
    {
      role: language === 'hi' ? 'School Success' : 'School Success',
      summary: language === 'hi' ? 'Onboarding, training, adoption aur go-live support' : 'Onboarding, training, adoption, and go-live support'
    }
  ];

  // Fetch all 31 AI features from API
  useEffect(() => {
    let cancelled = false;
    const fetchFeatures = async (attempt = 1) => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);
        const res = await fetch(`${API_URL}/api/lead-nurturing/features-drip`, { signal: controller.signal });
        clearTimeout(timeoutId);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        if (data.success && Array.isArray(data.features) && data.features.length > 0) {
          setAllFeatures(data.features);
          setFeaturesError(false);
        } else {
          throw new Error('Empty features payload');
        }
      } catch (err) {
        console.error('Error fetching features (attempt ' + attempt + '):', err);
        if (!cancelled && attempt < 3) {
          setTimeout(() => fetchFeatures(attempt + 1), 1500 * attempt);
          return;
        }
        if (!cancelled) setFeaturesError(true);
      } finally {
        if (!cancelled) setFeaturesLoading(false);
      }
    };
    fetchFeatures();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const fetchBrandContent = async () => {
      try {
        const res = await fetch(`${API_URL}/api/brand-pages/public`);
        const data = await res.json();
        if (data?.success) setBrandContent(data.content || null);
      } catch (err) {
        console.error('Error fetching brand content:', err);
      }
    };
    fetchBrandContent();
  }, []);

  const resolvedAboutCards = brandContent?.home_about_cards || aboutCards;
  const resolvedVisionTitle = brandContent?.vision_title || 'हर principal ko control, हर team ko clarity, हर parent ko trust';
  const resolvedVisionBody = brandContent?.vision_body || 'हमारा vision hai ki Indian schools fragmented tools aur manual stress se बाहर निकलकर एक unified AI-led system पर काम करें।';
  const resolvedMissionTitle = brandContent?.mission_title || 'School ko चलाना आसान, measurable aur growth-ready बनाना';
  const resolvedMissionBody = brandContent?.mission_body || 'हम mission पर हैं ki school leaders ka समय admin me waste na ho — balki वही समय admissions, student outcomes aur parent trust build karne me लगे।';
  const resolvedTeamMembers = brandContent?.team_members || teamPlaceholders;

  // Fetch detailed feature info when a feature is selected
  const fetchFeatureDetail = async (day) => {
    setFeatureDetailLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/lead-nurturing/feature-detail/${day}?language=${language}`);
      const data = await res.json();
      if (data.success) {
        setFeatureDetail(data.detail);
      }
    } catch (err) {
      console.error('Error fetching feature detail:', err);
    } finally {
      setFeatureDetailLoading(false);
    }
  };

  const openFeatureModal = (feature) => {
    setSelectedFeature(feature);
    fetchFeatureDetail(feature.day);
  };

  const navigateFeature = (direction) => {
    if (!selectedFeature) return;
    const newDay = direction === 'next' 
      ? (selectedFeature.day < 31 ? selectedFeature.day + 1 : 1)
      : (selectedFeature.day > 1 ? selectedFeature.day - 1 : 31);
    const newFeature = allFeatures.find(f => f.day === newDay);
    if (newFeature) {
      setSelectedFeature(newFeature);
      fetchFeatureDetail(newDay);
    }
  };

  const closeFeatureModal = () => {
    setSelectedFeature(null);
    setFeatureDetail(null);
  };

  // Helper function to navigate to AI feature page from Core Modules
  const openAIFeatureByName = (aiFeatureName) => {
    const featureNameMap = {
      'Attendance Alerts (AI)': 'Attendance Alert',
      'Fee Reminder AI': 'Fee Reminder AI',
      'AI Report Card': 'Report Card AI',
      'Smart Timetable (AI)': 'Smart Timetable Generator',
      'Communication Templates (AI)': 'Communication Templates'
    };
    
    const searchName = featureNameMap[aiFeatureName] || aiFeatureName;
    const feature = allFeatures.find(f => 
      f.feature.toLowerCase().includes(searchName.toLowerCase().replace(' (ai)', '').replace(' ai', ''))
    );
    
    if (feature) {
      const slug = DAY_TO_SLUG[feature.day];
      if (slug) {
        navigate(`/feature/${slug}`);
      } else {
        navigate('/features');
      }
    } else {
      navigate('/features');
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/leads/demo-request`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...formData, language, source: 'landing_page' }) });
    } catch (err) { console.error('Lead submission error:', err); }
    setFormSubmitted(true);
    setSubmitting(false);
  };

  const scrollToSection = (id) => { document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); };

  return (
    <div className="min-h-screen bg-slate-50 relative">
      {/* Background Grid Pattern */}
      <div className="fixed inset-0 z-0 opacity-30 pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, rgb(203 213 225 / 0.4) 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }}></div>
      
      {/* Decorative Glow Orbs */}
      <div className="fixed top-40 left-20 w-96 h-96 bg-orange-400/10 rounded-full blur-[120px] z-0 pointer-events-none"></div>
      <div className="fixed bottom-40 right-20 w-80 h-80 bg-indigo-500/10 rounded-full blur-[100px] z-0 pointer-events-none"></div>
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-b border-slate-200/50 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <img src="https://customer-assets.emergentagent.com/job_b71b88a0-de31-4e82-b047-c99df2477168/artifacts/7dlw52ts_logo%20%281%29.jpg" alt="Shiksha Mitra Logo" className="h-10 w-10 rounded-lg object-cover" />
              <span className="text-xl font-bold bg-gradient-to-r from-orange-500 to-green-600 bg-clip-text text-transparent" style={{fontFamily: 'Outfit, sans-serif'}}>शिक्षा मित्र</span>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => scrollToSection('features')} className="text-slate-600 hover:text-orange-500 transition font-medium">Features</button>
              <button onClick={() => scrollToSection('admission-engine')} className="text-slate-600 hover:text-orange-500 transition font-medium">Admission Engine</button>
              <button onClick={() => scrollToSection('pricing')} className="text-slate-600 hover:text-orange-500 transition font-medium">Pricing</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-slate-600 hover:text-orange-500 transition font-medium">Reviews</button>
              <button onClick={() => scrollToSection('about-home')} className="text-slate-600 hover:text-orange-500 transition font-medium">About</button>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <button onClick={() => setLanguage(language === 'hi' ? 'en' : 'hi')} className="flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 transition text-xs sm:text-sm font-medium">
                <span className={language === 'en' ? 'font-bold text-orange-500' : 'text-slate-500'}>EN</span><span className="text-slate-300">|</span><span className={language === 'hi' ? 'font-bold text-orange-500' : 'text-slate-500'}>हिं</span>
              </button>
              <button onClick={onLoginClick} className="px-3 sm:px-4 py-2 text-orange-500 hover:text-orange-600 font-semibold transition text-sm">Login</button>
              <button onClick={() => setShowDemoForm(true)} className="hidden sm:block px-5 py-2.5 bg-slate-900 text-white rounded-full hover:bg-slate-800 transition font-medium shadow-lg text-sm">{t.hero.cta_primary}</button>
              <button onClick={() => setShowDemoForm(true)} className="sm:hidden px-3 py-2 bg-slate-900 text-white rounded-full text-xs font-medium shadow-lg">Demo</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Floating Dark Island */}
      <section className="pt-28 pb-8 px-4 md:px-8 relative z-10">
        <div className="max-w-[95%] xl:max-w-7xl mx-auto">
          <div className="bg-slate-950 rounded-[2.5rem] md:rounded-[3rem] overflow-hidden relative shadow-2xl shadow-slate-900/30">
            {/* Background Image */}
            <div 
              className="absolute inset-0 z-0"
              style={{
                backgroundImage: `url('https://customer-assets.emergentagent.com/job_7fc4408d-deea-4514-b797-0a32a1d11f55/artifacts/ch7afdat_Gemini_Generated_Image_ylp2j1ylp2j1ylp2.png')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            />
            {/* Dark Overlay */}
            <div className="absolute inset-0 z-0 bg-gradient-to-b from-slate-950/80 via-slate-950/60 to-slate-950/90" />
            
            <div className="relative z-10 px-8 md:px-16 lg:px-24 py-16 md:py-24">
              <div className="text-center max-w-4xl mx-auto">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md text-white border border-white/20 rounded-full text-sm font-medium mb-6">
                  <span className="text-yellow-400">🏆</span> {t.hero.badge}
                </div>
                
                {/* Title */}
                <h1 className="text-4xl sm:text-5xl md:text-7xl font-black mb-6 tracking-tight text-white" style={{fontFamily: 'Outfit, sans-serif', textShadow: '0 4px 30px rgba(0,0,0,0.5)'}}>
                  {t.hero.title}
                </h1>
                
                {/* Subtitle */}
                <p className="text-lg md:text-2xl text-white/90 mb-4 font-medium" style={{fontFamily: 'Manrope, sans-serif'}}>
                  {t.hero.subtitle}
                </p>
                
                {/* Description */}
                <p className="text-base md:text-lg text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed" style={{fontFamily: 'Manrope, sans-serif'}}>
                  {t.hero.description}
                </p>
                
                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
                  <button 
                    onClick={() => setShowDemoForm(true)} 
                    className="px-8 py-4 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-all font-semibold text-lg shadow-xl shadow-orange-500/30 transform hover:scale-105 hover:-translate-y-1"
                    data-testid="hero-cta-primary"
                  >
                    {t.hero.cta_primary} →
                  </button>
                  <Link 
                    to="/school-match"
                    className="px-8 py-4 bg-green-500 text-white rounded-full hover:bg-green-600 transition-all font-semibold text-lg shadow-xl shadow-green-500/30 transform hover:scale-105 hover:-translate-y-1 text-center"
                    data-testid="hero-find-school"
                  >
                    🎓 {language === 'hi' ? 'स्कूल खोजें' : 'Find School'}
                  </Link>
                  <button 
                    onClick={() => scrollToSection('features')} 
                    className="px-8 py-4 bg-white/10 backdrop-blur-md border border-white/30 text-white rounded-full hover:bg-white/20 transition-all font-semibold text-lg"
                    data-testid="hero-cta-secondary"
                  >
                    {t.hero.cta_secondary}
                  </button>
                </div>
                
                {/* Stats Cards - Floating outside the island */}
              </div>
            </div>
          </div>
          
          {/* Stats Cards - Overlapping the island */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 -mt-12 md:-mt-16 relative z-20 px-4 md:px-12">
            {t.hero.stats.map((stat, idx) => (
              <div 
                key={idx} 
                className="bg-white rounded-2xl p-5 md:p-6 shadow-xl border border-slate-100 transform hover:scale-105 hover:-translate-y-1 transition-all duration-300 group"
                data-testid={`hero-stat-${idx}`}
              >
                <div className="text-2xl md:text-4xl font-black text-slate-900 group-hover:text-orange-500 transition-colors" style={{fontFamily: 'Outfit, sans-serif'}}>
                  {stat.value}
                </div>
                <div className="text-slate-500 text-xs md:text-sm mt-1 font-medium" style={{fontFamily: 'Manrope, sans-serif'}}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* "Is Your School In Our Database?" — Marketing Hook */}
      <section className="py-10 px-4 md:px-8 relative z-10" data-testid="my-school-hook-section">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-3xl p-6 md:p-8 shadow-2xl overflow-hidden relative">
            <div className="absolute -top-10 -right-10 w-48 h-48 bg-yellow-300/20 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-cyan-300/20 rounded-full blur-3xl pointer-events-none"></div>

            <div className="relative flex flex-col md:flex-row items-center gap-6">
              <div className="flex-1 text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-yellow-300 text-slate-900 rounded-full text-xs font-black uppercase tracking-wider mb-3 shadow-lg">
                  <i className="fa-solid fa-database"></i>
                  {language === 'hi' ? '3.9 लाख Schools Database' : '3.9 Lakh Schools Database'}
                </div>
                <h3 className="text-2xl md:text-4xl font-black text-white mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
                  {language === 'hi' ? 'क्या आपका School हमारे Database में है?' : 'Is Your School In Our Database?'}
                </h3>
                <p className="text-white/90 text-sm md:text-base max-w-xl">
                  {language === 'hi' 
                    ? 'अपना UDISE code डालें — 30 सेकंड में पता करें कि आपकी school की 50+ details हमारे पास पहले से हैं। Signup होते ही सब auto-fill हो जाएगा!'
                    : 'Enter your UDISE code — find out in 30 seconds which of your 50+ school details we already have. Everything auto-fills on signup!'}
                </p>
              </div>

              <div className="flex-shrink-0">
                <Link
                  to="/my-school"
                  className="inline-flex items-center gap-2 px-7 py-4 bg-white text-slate-900 rounded-full font-black hover:bg-slate-100 transition shadow-xl text-base md:text-lg whitespace-nowrap"
                  data-testid="check-my-school-cta"
                >
                  🔍 {language === 'hi' ? 'मेरी School Check करें' : 'Check My School'} →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problems Section - Bento Grid on Light Background */}
      <section className="py-24 px-4 md:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-red-100 text-red-600 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider" style={{fontFamily: 'Manrope, sans-serif'}}>
              {language === 'hi' ? 'समस्याएं' : 'Problems'}
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>
              {t.problems.title}
            </h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto" style={{fontFamily: 'Manrope, sans-serif'}}>
              {language === 'hi' ? '😟 स्कूल चलाना आसान नहीं है...' : '😟 Running a school is not easy...'}
            </p>
          </div>
          
          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {t.problems.items.map((item, idx) => {
              // Asymmetric grid pattern
              const gridClasses = [
                'md:col-span-8', 'md:col-span-4', 'md:col-span-4', 
                'md:col-span-4', 'md:col-span-4', 'md:col-span-6 md:col-start-4'
              ];
              const isLarge = idx === 0 || idx === 5;
              
              return (
                <div 
                  key={idx} 
                  className={`${gridClasses[idx] || 'md:col-span-4'} bg-white rounded-3xl p-8 border border-slate-200 shadow-sm hover:shadow-xl hover:border-red-200 transition-all duration-300 transform hover:-translate-y-1 group`}
                  data-testid={`problem-card-${idx}`}
                >
                  <div className={`${isLarge ? 'flex items-start gap-6' : ''}`}>
                    <div className={`${isLarge ? 'w-20 h-20' : 'w-14 h-14'} bg-red-100 rounded-2xl flex items-center justify-center text-3xl ${isLarge ? 'text-4xl' : ''} mb-5 ${isLarge ? 'mb-0 flex-shrink-0' : ''} group-hover:scale-110 transition-transform`}>
                      {item.icon}
                    </div>
                    <div>
                      <h3 className={`${isLarge ? 'text-2xl' : 'text-xl'} font-bold text-slate-900 mb-3`} style={{fontFamily: 'Outfit, sans-serif'}}>
                        {item.title}
                      </h3>
                      <p className="text-slate-500 leading-relaxed" style={{fontFamily: 'Manrope, sans-serif'}}>{item.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Before vs After - Floating Dark Island */}
      <section className="py-8 px-4 md:px-8 relative z-10">
        <div className="max-w-[95%] xl:max-w-7xl mx-auto">
          <div className="bg-slate-950 rounded-[2.5rem] md:rounded-[3rem] p-8 md:p-12 lg:p-16 shadow-2xl shadow-slate-900/20">
            {/* Section Header */}
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-white/10 text-white/80 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider border border-white/20">
                {language === 'hi' ? '✨ बदलाव देखें' : '✨ See the Difference'}
              </span>
              <h2 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
                {language === 'hi' ? 'देखिए बदलाव!' : 'See the Transformation!'}
              </h2>
              <p className="text-lg text-white/60" style={{fontFamily: 'Manrope, sans-serif'}}>
                {language === 'hi' ? 'शिक्षा मित्र से पहले और बाद में' : 'Before and After Shiksha Mitra'}
              </p>
            </div>
            
            {/* Before vs After Visual Comparison */}
            <div className="relative">
              {/* Desktop VS Badge */}
              <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-30">
                <div className="bg-orange-500 text-white font-black text-xl w-16 h-16 rounded-full flex items-center justify-center shadow-2xl border-4 border-white">
                  VS
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 md:gap-10">
                {/* BEFORE Column */}
                <div className="relative">
                  <div className="absolute -top-3 left-4 z-20">
                    <span className="px-5 py-2 bg-red-500 text-white font-bold rounded-full text-sm shadow-lg">
                      😟 {language === 'hi' ? 'पहले' : 'BEFORE'}
                    </span>
                  </div>
                  
                  <div className="relative rounded-2xl overflow-hidden bg-white/5 border border-white/10">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_7fc4408d-deea-4514-b797-0a32a1d11f55/artifacts/kgwjo05z_Gemini_Generated_Image_j2s0owj2s0owj2s0.png"
                      alt="Before Shiksha Mitra"
                      className="w-full h-[350px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/40 to-transparent"></div>
                    
                    {/* Floating Tags with Animation */}
                    <div className="absolute top-6 right-4 animate-bounce" style={{animationDelay: '0s', animationDuration: '2s'}}><span className="px-3 py-1.5 bg-red-500/90 text-white text-xs font-medium rounded-full shadow-lg">📝 Manual Register</span></div>
                    <div className="absolute top-16 left-4 animate-bounce" style={{animationDelay: '0.3s', animationDuration: '2.2s'}}><span className="px-3 py-1.5 bg-red-500/90 text-white text-xs font-medium rounded-full shadow-lg">😫 UDISE+ उलझन</span></div>
                    <div className="absolute top-28 right-6 animate-bounce" style={{animationDelay: '0.6s', animationDuration: '2.4s'}}><span className="px-3 py-1.5 bg-red-500/90 text-white text-xs font-medium rounded-full shadow-lg">📞 Endless Calls</span></div>
                    <div className="absolute bottom-24 left-4 animate-bounce" style={{animationDelay: '0.9s', animationDuration: '2.6s'}}><span className="px-3 py-1.5 bg-red-500/90 text-white text-xs font-medium rounded-full shadow-lg">💸 Fee Tension</span></div>
                    
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="text-xl font-bold text-white mb-1" style={{fontFamily: 'Outfit, sans-serif'}}>😰 {language === 'hi' ? 'परेशान Principal' : 'Stressed Principal'}</h3>
                      <p className="text-red-300 text-sm">{language === 'hi' ? 'हर दिन नई समस्या' : 'New problems every day'}</p>
                    </div>
                  </div>
                </div>
                
                {/* Mobile VS Badge */}
                <div className="md:hidden flex items-center justify-center -my-4 relative z-20">
                  <div className="bg-orange-500 text-white font-bold text-lg w-12 h-12 rounded-full flex items-center justify-center shadow-xl border-4 border-white">VS</div>
                </div>
                
                {/* AFTER Column */}
                <div className="relative">
                  <div className="absolute -top-3 right-4 z-20">
                    <span className="px-5 py-2 bg-green-500 text-white font-bold rounded-full text-sm shadow-lg">
                      😊 {language === 'hi' ? 'बाद में' : 'AFTER'}
                    </span>
                  </div>
                  
                  <div className="relative rounded-2xl overflow-hidden bg-white/5 border border-white/10">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_7fc4408d-deea-4514-b797-0a32a1d11f55/artifacts/htyqv46f_Gemini_Generated_Image_dc9r9ddc9r9ddc9r.png"
                      alt="After Shiksha Mitra"
                      className="w-full h-[350px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/40 to-transparent"></div>
                    
                    {/* Floating Tags with Animation */}
                    <div className="absolute top-6 left-4 animate-bounce" style={{animationDelay: '0.2s', animationDuration: '2.1s'}}><span className="px-3 py-1.5 bg-green-500/90 text-white text-xs font-medium rounded-full shadow-lg">🤖 AI Notice Writer</span></div>
                    <div className="absolute top-16 right-4 animate-bounce" style={{animationDelay: '0.5s', animationDuration: '2.3s'}}><span className="px-3 py-1.5 bg-green-500/90 text-white text-xs font-medium rounded-full shadow-lg">📊 Auto UDISE+</span></div>
                    <div className="absolute top-28 left-6 animate-bounce" style={{animationDelay: '0.8s', animationDuration: '2.5s'}}><span className="px-3 py-1.5 bg-green-500/90 text-white text-xs font-medium rounded-full shadow-lg">📱 WhatsApp Alerts</span></div>
                    <div className="absolute bottom-24 right-4 animate-bounce" style={{animationDelay: '1.1s', animationDuration: '2.7s'}}><span className="px-3 py-1.5 bg-green-500/90 text-white text-xs font-medium rounded-full shadow-lg">💰 Digital Fee</span></div>
                    
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="text-xl font-bold text-white mb-1" style={{fontFamily: 'Outfit, sans-serif'}}>😊 {language === 'hi' ? 'खुश Principal' : 'Happy Principal'}</h3>
                      <p className="text-green-300 text-sm">{language === 'hi' ? 'सब automatic, 10x time saved' : 'Everything automatic'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Stats Bar */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: '⏱️', value: '10x', label: language === 'hi' ? 'तेज़ काम' : 'Faster Work' },
                { icon: '📉', value: '90%', label: language === 'hi' ? 'कम Stress' : 'Less Stress' },
                { icon: '💰', value: '2x', label: language === 'hi' ? 'Fee Collection' : 'Fee Collection' },
                { icon: '😊', value: '100%', label: language === 'hi' ? 'संतुष्टि' : 'Satisfaction' },
              ].map((stat, idx) => (
                <div key={idx} className="bg-white/5 rounded-2xl p-5 text-center border border-white/10 hover:bg-white/10 transition-all">
                  <div className="text-2xl mb-1">{stat.icon}</div>
                  <div className="text-2xl font-black text-orange-400" style={{fontFamily: 'Outfit, sans-serif'}}>{stat.value}</div>
                  <div className="text-sm text-white/60">{stat.label}</div>
                </div>
              ))}
            </div>
            
            {/* CTA */}
            <div className="text-center mt-10">
              <button 
                onClick={() => setShowDemoForm(true)}
                className="px-10 py-4 bg-green-500 text-white rounded-full hover:bg-green-600 transition-all font-bold text-lg shadow-lg shadow-green-500/30 transform hover:scale-105"
                data-testid="before-after-cta-btn"
              >
                {language === 'hi' ? '🚀 आप भी बदलाव देखें!' : '🚀 See the Change!'} →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* NEW: Operational ERP Modules Showcase */}
      <section className="py-24 px-4 md:px-8 relative z-10" data-testid="erp-modules-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-emerald-100 text-emerald-600 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider" style={{fontFamily: 'Manrope, sans-serif'}}>
              {language === 'hi' ? '🏫 ERP Modules — Live' : '🏫 ERP Modules — Live'}
            </span>
            <h2 className="text-3xl md:text-5xl font-black mb-4 text-slate-900 tracking-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
              {language === 'hi' ? 'सब कुछ एक Platform पर' : 'Everything on One Platform'}
            </h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto" style={{fontFamily: 'Manrope, sans-serif'}}>
              {language === 'hi' ? 'All Other ERP को replace करें — AI-powered operational modules के साथ' : 'Replace all other ERP tools — with AI-powered operational modules'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                icon: '✅', title: language === 'hi' ? 'Attendance Management' : 'Attendance Management',
                desc: language === 'hi' ? 'Biometric/RFID + Manual, दैनिक रिपोर्ट, AI Alerts' : 'Biometric/RFID + Manual, Daily Reports, AI Alerts',
                color: 'from-green-500 to-emerald-600', tag: 'LIVE'
              },
              {
                icon: '📝', title: language === 'hi' ? 'Exam & Report Cards' : 'Exam & Report Cards',
                desc: language === 'hi' ? 'AI Report Card, WhatsApp Share, Marks Entry' : 'AI Report Card, WhatsApp Share, Marks Entry',
                color: 'from-blue-500 to-indigo-600', tag: 'LIVE'
              },
              {
                icon: '📅', title: language === 'hi' ? 'Smart Timetable' : 'Smart Timetable',
                desc: language === 'hi' ? 'Auto-generate, Conflict Detection, Teacher Load Balancing' : 'Auto-generate, Conflict Detection, Teacher Load Balancing',
                color: 'from-violet-500 to-purple-600', tag: 'AI'
              },
              {
                icon: '🚌', title: language === 'hi' ? 'Transport & GPS' : 'Transport & GPS',
                desc: language === 'hi' ? 'Live Bus Tracking, Route Management, Parent Portal' : 'Live Bus Tracking, Route Management, Parent Portal',
                color: 'from-cyan-500 to-blue-600', tag: 'LIVE'
              },
              {
                icon: '👨‍💼', title: language === 'hi' ? 'HR & Payroll' : 'HR & Payroll',
                desc: language === 'hi' ? 'Salary Structure, Payslip Generate, WhatsApp Share' : 'Salary Structure, Payslip Generate, WhatsApp Share',
                color: 'from-orange-500 to-red-500', tag: 'LIVE'
              },
              {
                icon: '📖', title: language === 'hi' ? 'Library Management' : 'Library Management',
                desc: language === 'hi' ? 'Barcode Scanner, Issue/Return, Analytics, PDF Report' : 'Barcode Scanner, Issue/Return, Analytics, PDF Report',
                color: 'from-amber-500 to-orange-600', tag: 'NEW'
              },
              {
                icon: '🔔', title: language === 'hi' ? 'Notification System' : 'Notification System',
                desc: language === 'hi' ? 'Real-time Alerts, Auto-triggers, Bell Icon' : 'Real-time Alerts, Auto-triggers, Bell Icon',
                color: 'from-rose-500 to-pink-600', tag: 'NEW'
              },
              {
                icon: '🌐', title: language === 'hi' ? 'Custom Domain' : 'Custom Domain',
                desc: language === 'hi' ? 'अपना Domain Map करें, SSL Auto, DNS Verify' : 'Map Your Domain, Auto SSL, DNS Verify',
                color: 'from-teal-500 to-emerald-600', tag: 'NEW'
              },
            ].map((mod, idx) => (
              <div key={idx} className="group relative bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-xl hover:border-slate-300 transition-all duration-300 transform hover:-translate-y-1" data-testid={`erp-module-${idx}`}>
                {/* Tag */}
                <span className={`absolute top-3 right-3 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  mod.tag === 'NEW' ? 'bg-orange-100 text-orange-600' : mod.tag === 'AI' ? 'bg-purple-100 text-purple-600' : 'bg-green-100 text-green-600'
                }`}>{mod.tag}</span>
                {/* Icon */}
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${mod.color} flex items-center justify-center text-2xl text-white mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                  {mod.icon}
                </div>
                <h3 className="font-bold text-slate-900 text-lg mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>{mod.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed" style={{fontFamily: 'Manrope, sans-serif'}}>{mod.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <button
              onClick={() => setShowDemoForm(true)}
              className="px-8 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-full font-semibold hover:shadow-xl hover:shadow-orange-500/20 transition-all transform hover:scale-105"
              data-testid="erp-cta-demo"
            >
              {language === 'hi' ? '🚀 Free Demo बुक करें' : '🚀 Book Free Demo'}
            </button>
          </div>
        </div>
      </section>

      {/* Solutions Section - Light Background with Images */}
      <section className="py-24 px-4 md:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-orange-100 text-orange-600 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider" style={{fontFamily: 'Manrope, sans-serif'}}>
              {language === 'hi' ? 'AI Solutions' : 'AI Solutions'}
            </span>
            <h2 className="text-3xl md:text-5xl font-black mb-4 text-slate-900 tracking-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
              {t.solutions.title}
            </h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto" style={{fontFamily: 'Manrope, sans-serif'}}>
              {t.solutions.subtitle}
            </p>
          </div>
          
          {/* Solutions Bento Grid with Images */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {t.solutions.items.map((item, idx) => {
              const gridClasses = ['md:col-span-6', 'md:col-span-6', 'md:col-span-4', 'md:col-span-4', 'md:col-span-4', 'md:col-span-12'];
              const isWide = idx === 5;
              
              // Map solution titles to AI feature names
              const featureNameMap = {
                'AI Notice Writer': 'Notice Simplifier',
                'One-Click Reports': 'Report Card AI',
                'WhatsApp Integration': 'Fee Reminder AI',
                'Parent App': 'Parent Daily Summary',
                'Online Fees': 'Fee Reminder AI',
                'AI Tutor': 'Chapter Explainer'
              };
              
              const handleSolutionClick = () => {
                const searchName = featureNameMap[item.title] || item.title;
                const feature = allFeatures.find(f => 
                  f.feature.toLowerCase().includes(searchName.toLowerCase())
                );
                
                if (feature) {
                  const slug = DAY_TO_SLUG[feature.day];
                  if (slug) {
                    navigate(`/feature/${slug}`);
                  } else {
                    navigate('/features');
                  }
                } else {
                  navigate('/features');
                }
              };
              
              return (
                <div 
                  key={idx} 
                  className={`${gridClasses[idx]} group bg-white rounded-3xl border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex ${isWide ? 'flex-row' : 'flex-col'} hover:-translate-y-1 cursor-pointer`}
                  data-testid={`solution-card-${idx}`}
                  onClick={handleSolutionClick}
                >
                  {/* Image Container */}
                  <div className={`${isWide ? 'w-1/3 h-auto' : 'h-44'} overflow-hidden relative flex-shrink-0`}>
                    <img 
                      src={item.image} 
                      alt={item.title}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    />
                    {!isWide && (
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1.5 bg-orange-500 text-white text-xs font-bold rounded-full shadow-lg">
                          {item.tag}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="p-6 flex flex-col flex-grow">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center text-2xl">
                        {item.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-slate-900 group-hover:text-orange-600 transition-colors" style={{fontFamily: 'Outfit, sans-serif'}}>
                          {item.title}
                        </h3>
                        {isWide && <span className="text-xs font-bold text-orange-500">{item.tag}</span>}
                      </div>
                    </div>
                    <p className="text-slate-500 leading-relaxed flex-grow" style={{fontFamily: 'Manrope, sans-serif'}}>{item.desc}</p>
                    <div className="mt-4 flex items-center text-orange-500 font-medium group-hover:text-orange-600">
                      <span className="text-sm">{language === 'hi' ? 'और जानें' : 'Learn more'}</span>
                      <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Bottom CTA */}
          <div className="text-center mt-16">
            <p className="text-slate-400 mb-6" style={{fontFamily: 'Manrope, sans-serif'}}>
              {language === 'hi' ? 'और भी बहुत कुछ है... 31 AI Features explore करें' : 'And much more... Explore all 31 AI Features'}
            </p>
            <button
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-full shadow-lg transition-all hover:-translate-y-1"
              data-testid="view-all-features-btn"
            >
              <span>{language === 'hi' ? 'सभी Features देखें' : 'View All Features'}</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Admission Engine - Star Feature Section */}
      <section id="admission-engine" className="py-8 px-4 md:px-8 relative z-10">
        <div className="max-w-[95%] xl:max-w-7xl mx-auto">
          <div className="bg-slate-950 rounded-[2.5rem] md:rounded-[3rem] p-8 md:p-12 lg:p-16 shadow-2xl shadow-slate-900/20 relative overflow-hidden">
            {/* Subtle Accent Glow */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/8 rounded-full blur-[120px] pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-72 h-72 bg-teal-500/8 rounded-full blur-[100px] pointer-events-none"></div>
            
            <div className="relative z-10">
              {/* Header */}
              <div className="text-center mb-10">
                <span className="inline-block px-5 py-2 bg-orange-500/15 text-orange-400 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider border border-orange-500/25" style={{fontFamily: 'Manrope, sans-serif'}}>
                  {language === 'hi' ? 'सबसे Powerful Feature' : 'Most Powerful Feature'}
                </span>
                <h2 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
                  {language === 'hi' ? 'Admission Engine — स्मार्ट प्रवेश प्रणाली' : 'Admission Engine'}
                </h2>
                <p className="text-lg text-white/60 max-w-2xl mx-auto" style={{fontFamily: 'Manrope, sans-serif'}}>
                  {language === 'hi' 
                    ? 'AI-Powered Smart Admission System — Seat Planning से Fee Negotiation तक, सब Automated!'
                    : 'AI-Powered Smart Admission System — From Seat Planning to Fee Negotiation, Everything Automated!'}
                </p>
              </div>

              {/* Feature Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-6">
                {[
                  { 
                    icon: 'fa-chair', 
                    title: language === 'hi' ? 'स्मार्ट सीट काउंटर' : 'Smart Seat Counter',
                    desc: language === 'hi' ? 'Real-time seat availability' : 'Real-time seat tracking',
                    color: 'orange'
                  },
                  { 
                    icon: 'fa-magnifying-glass-location', 
                    title: language === 'hi' ? 'स्कूल खोजें' : 'School Finder',
                    desc: language === 'hi' ? 'Area के schools ढूंढें' : 'Find schools by area',
                    color: 'blue'
                  },
                  { 
                    icon: 'fa-gamepad', 
                    title: language === 'hi' ? 'AI Entrance Exam' : 'AI Entrance Exam',
                    desc: language === 'hi' ? 'Fun game-style exam — बच्चे को exam नहीं, game लगे' : 'Fun game-style assessment for kids',
                    color: 'rose'
                  },
                  { 
                    icon: 'fa-location-crosshairs', 
                    title: language === 'hi' ? 'Geo-Fence मार्केटिंग' : 'Geo-Fence Marketing',
                    desc: language === 'hi' ? '5KM radius में auto-ads' : 'Auto-target within 5KM',
                    color: 'green'
                  },
                  { 
                    icon: 'fa-handshake', 
                    title: language === 'hi' ? 'AI फीस वार्ता' : 'AI Fee Negotiator',
                    desc: language === 'hi' ? 'Income-based scholarship suggest' : 'Income-based scholarships',
                    color: 'purple'
                  },
                  { 
                    icon: 'fa-film', 
                    title: language === 'hi' ? 'Welcome वीडियो' : 'Welcome Video',
                    desc: language === 'hi' ? 'Personalized welcome video' : 'Auto-generate welcome videos',
                    color: 'amber'
                  },
                ].map((feat, i) => {
                  const colorMap = {
                    orange: { border: 'border-orange-200 hover:border-orange-400', iconBg: 'bg-orange-500' },
                    blue: { border: 'border-blue-200 hover:border-blue-400', iconBg: 'bg-blue-500' },
                    green: { border: 'border-green-200 hover:border-green-400', iconBg: 'bg-green-500' },
                    purple: { border: 'border-purple-200 hover:border-purple-400', iconBg: 'bg-purple-500' },
                    amber: { border: 'border-amber-200 hover:border-amber-400', iconBg: 'bg-amber-500' },
                    rose: { border: 'border-rose-200 hover:border-rose-400', iconBg: 'bg-rose-500' },
                  };
                  const c = colorMap[feat.color];
                  return (
                    <div key={i} className={`bg-white rounded-2xl p-4 border-2 ${c.border} transition-all duration-300 group hover:scale-[1.03] hover:shadow-xl`} data-testid={`admission-feature-${i}`}>
                      <div className={`w-10 h-10 rounded-xl ${c.iconBg} flex items-center justify-center mb-2 group-hover:scale-110 transition-transform`}>
                        <i className={`fa-solid ${feat.icon} text-white`}></i>
                      </div>
                      <h4 className="font-bold text-slate-800 text-xs mb-0.5" style={{fontFamily: 'Outfit, sans-serif'}}>{feat.title}</h4>
                      <p className="text-slate-500 text-[10px] leading-relaxed" style={{fontFamily: 'Manrope, sans-serif'}}>{feat.desc}</p>
                    </div>
                  );
                })}
              </div>

              {/* Portal CTA */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
                <a href="/admission-portal" className="px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold text-sm hover:bg-orange-600 transition text-center" data-testid="portal-cta-btn">
                  <i className="fa-solid fa-graduation-cap mr-2"></i>
                  {language === 'hi' ? 'Admission Portal — Parents के लिए' : 'Admission Portal — For Parents'}
                </a>
              </div>

              {/* Bottom Flow - Admission Journey */}
              <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                <p className="text-center text-white/40 text-xs font-semibold uppercase tracking-wider mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>
                  {language === 'hi' ? 'सम्पूर्ण Admission यात्रा' : 'Complete Admission Journey'}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-1.5 md:gap-3">
                  {[
                    language === 'hi' ? 'स्कूल खोजें' : 'Parent Search',
                    language === 'hi' ? 'सीट जांचें' : 'Seat Check',
                    language === 'hi' ? 'Online आवेदन' : 'Online Apply',
                    language === 'hi' ? 'फीस वार्ता' : 'Fee Negotiation',
                    language === 'hi' ? 'दस्तावेज़ Upload' : 'Document Upload',
                    language === 'hi' ? 'प्रवेश पक्का' : 'Admission Confirm',
                    language === 'hi' ? 'Welcome वीडियो' : 'Welcome Video',
                  ].map((step, i) => (
                    <React.Fragment key={i}>
                      <span className="px-3 py-1.5 bg-white/10 text-white/80 text-xs font-medium rounded-full border border-white/15">{step}</span>
                      {i < 6 && <i className="fa-solid fa-chevron-right text-orange-500/50 text-[10px] hidden md:inline"></i>}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - All 31 AI Features - Floating Dark Island */}
      <section id="features" className="py-8 px-4 md:px-8 relative z-10">
        <div className="max-w-[95%] xl:max-w-7xl mx-auto">
          <div className="bg-slate-950 rounded-[2.5rem] md:rounded-[3rem] p-8 md:p-12 lg:p-16 shadow-2xl shadow-slate-900/20">
            <h2 className="text-3xl md:text-5xl font-black text-center mb-4 text-white" style={{fontFamily: 'Outfit, sans-serif'}}>{t.features.title}</h2>
            <p className="text-center text-white/60 mb-10" style={{fontFamily: 'Manrope, sans-serif'}}>{language === 'hi' ? 'हर Feature पर click करके पूरी जानकारी देखें' : 'Click on any feature to see full details'}</p>
            
            {/* Category Filter Tabs */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {[
                { key: 'all', label: language === 'hi' ? 'सभी (31)' : 'All (31)', icon: '🎯' },
                { key: 'admin', label: language === 'hi' ? 'Admin' : 'Admin', icon: '🏫' },
                { key: 'teacher', label: language === 'hi' ? 'Teacher' : 'Teacher', icon: '👨‍🏫' },
                { key: 'student', label: language === 'hi' ? 'Student' : 'Student', icon: '👨‍🎓' },
                { key: 'parent', label: language === 'hi' ? 'Parent' : 'Parent', icon: '👨‍👩‍👧' }
              ].map(cat => (
                <button
                  key={cat.key}
                  onClick={() => setActiveCategory(cat.key)}
                  className={`px-6 py-2.5 rounded-full font-medium transition-all flex items-center gap-2 ${
                    activeCategory === cat.key 
                      ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30' 
                      : 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
                  }`}
              >
                <span>{cat.icon}</span>
                <span>{cat.label}</span>
              </button>
            ))}
          </div>

          {/* Features Grid */}
          {featuresLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
            </div>
          ) : featuresError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="features-error-state">
              <div className="text-white/80 text-lg mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>
                {language === 'hi' ? 'AI Features load नहीं हो पाए। कृपया दोबारा कोशिश करें।' : 'AI Features could not load. Please try again.'}
              </div>
              <button
                onClick={() => { setFeaturesLoading(true); setFeaturesError(false); window.location.reload(); }}
                className="px-6 py-2.5 bg-orange-500 text-white rounded-full font-semibold hover:bg-orange-600 transition-all shadow-lg shadow-orange-500/30"
                data-testid="features-retry-btn"
              >
                {language === 'hi' ? '🔄 दोबारा कोशिश करें' : '🔄 Retry'}
              </button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {allFeatures
                .filter(f => activeCategory === 'all' || f.category === activeCategory)
                .map((feature, idx) => {
                  const categoryColors = {
                    admin: 'border-orange-200 hover:border-orange-400',
                    teacher: 'border-blue-200 hover:border-blue-400',
                    student: 'border-green-200 hover:border-green-400',
                    parent: 'border-purple-200 hover:border-purple-400',
                    all: 'border-amber-200 hover:border-amber-400'
                  };
                  const categoryBgColors = {
                    admin: 'bg-orange-100',
                    teacher: 'bg-blue-100',
                    student: 'bg-green-100',
                    parent: 'bg-purple-100',
                    all: 'bg-amber-100'
                  };
                  const categoryIcons = { admin: '🏫', teacher: '👨‍🏫', student: '👨‍🎓', parent: '👨‍👩‍👧', all: '🎯' };
                  const categoryTextColors = { admin: 'text-orange-600', teacher: 'text-blue-600', student: 'text-green-600', parent: 'text-purple-600', all: 'text-amber-600' };
                  
                  return (
                    <div 
                      key={feature.day}
                      onClick={() => {
                        const slug = DAY_TO_SLUG[feature.day];
                        if (slug) {
                          navigate(`/feature/${slug}`);
                        } else {
                          navigate('/features');
                        }
                      }}
                      className={`group bg-white rounded-2xl border-2 cursor-pointer transition-all transform hover:scale-[1.02] hover:shadow-xl overflow-hidden ${categoryColors[feature.category]}`}
                      data-testid={`feature-card-${feature.day}`}
                    >
                      {/* Image Container */}
                      {feature.hero_image && (
                        <div className="h-36 w-full overflow-hidden relative bg-slate-100">
                          <img 
                            src={feature.hero_image} 
                            alt={feature.feature}
                            loading="lazy"
                            decoding="async"
                            width="400"
                            height="144"
                            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                          />
                          {/* Day Badge */}
                          <div className="absolute top-3 right-3">
                            <span className={`text-xs font-bold px-2.5 py-1 rounded-full bg-white/90 shadow ${categoryTextColors[feature.category]}`}>
                              Day {feature.day}
                            </span>
                          </div>
                          {/* Category Icon */}
                          <div className="absolute top-3 left-3">
                            <span className={`text-xl w-8 h-8 flex items-center justify-center rounded-full ${categoryBgColors[feature.category]} shadow`}>
                              {categoryIcons[feature.category]}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* Content */}
                      <div className="p-5">
                        <h3 className={`font-bold text-lg mb-2 ${categoryTextColors[feature.category]}`} style={{fontFamily: 'Manrope, sans-serif'}}>
                          {feature.feature}
                        </h3>
                        <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                          {language === 'hi' ? feature.title_hi : feature.title_en}
                        </p>
                        <button className="text-sm text-orange-500 font-medium flex items-center gap-1 hover:text-orange-600 transition-colors">
                          {language === 'hi' ? 'विस्तार से देखें' : 'View Details'} 
                          <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}

          <div className="text-center mt-10">
            <button 
              onClick={() => setShowDemoForm(true)} 
              className="px-8 py-4 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition font-semibold text-lg shadow-xl shadow-orange-500/30"
            >
              {t.hero.cta_primary} →
            </button>
          </div>
        </div>
        </div>
      </section>

      {/* AI Model-wise Feature Comparison Table */}
      <section id="feature-comparison" className="py-20 px-4 md:px-8 relative z-10" data-testid="feature-comparison-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-indigo-100 text-indigo-600 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider" style={{fontFamily: 'Manrope, sans-serif'}}>
              {language === 'hi' ? '🧠 AI Model तुलना' : '🧠 AI Model Comparison'}
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>
              {language === 'hi' ? 'हर Feature के लिए सही AI Model' : 'Right AI Model for Every Feature'}
            </h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto" style={{fontFamily: 'Manrope, sans-serif'}}>
              {language === 'hi' 
                ? 'आपकी Gemini API Key पर — Flash-Lite (तेज़), Flash (Balanced), Pro (सर्वश्रेष्ठ)' 
                : 'On your Gemini API Key — Flash-Lite (Fast), Flash (Balanced), Pro (Best Quality)'}
            </p>
          </div>

          {/* Model Legend */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-10 h-10 bg-green-500 text-white rounded-xl flex items-center justify-center font-bold">T1</span>
                <h4 className="font-bold text-green-800" style={{fontFamily: 'Outfit, sans-serif'}}>Gemini Flash-Lite</h4>
              </div>
              <p className="text-sm text-green-700">{language === 'hi' ? 'तेज़, सस्ते और high-volume काम (reminders, alerts, templates)' : 'Fast, cheap, high-volume tasks (reminders, alerts, templates)'}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-10 h-10 bg-blue-500 text-white rounded-xl flex items-center justify-center font-bold">T2</span>
                <h4 className="font-bold text-blue-800" style={{fontFamily: 'Outfit, sans-serif'}}>Gemini Flash</h4>
              </div>
              <p className="text-sm text-blue-700">{language === 'hi' ? 'Balanced - ज्यादातर Features (MCQ, Report Card, Notices)' : 'Balanced - Most features (MCQ, Report Card, Notices)'}</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-2xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-10 h-10 bg-purple-500 text-white rounded-xl flex items-center justify-center font-bold">T3</span>
                <h4 className="font-bold text-purple-800" style={{fontFamily: 'Outfit, sans-serif'}}>Gemini Pro</h4>
              </div>
              <p className="text-sm text-purple-700">{language === 'hi' ? 'सर्वश्रेष्ठ - Critical analysis (At-Risk Alerts, Question Paper)' : 'Best - Critical analysis (At-Risk, Question Paper)'}</p>
            </div>
          </div>

          {/* Comparison Grid */}
          <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white px-6 py-4 hidden md:grid md:grid-cols-[2.2fr_1fr_1fr_0.9fr] gap-4 text-sm font-semibold" data-testid="landing-feature-comparison-header">
              <div>{language === 'hi' ? 'Feature' : 'Feature'}</div>
              <div className="text-center">{language === 'hi' ? 'Category' : 'Category'}</div>
              <div className="text-center">{language === 'hi' ? 'AI Model' : 'AI Model'}</div>
              <div className="text-center">{language === 'hi' ? 'Cost' : 'Cost'}</div>
            </div>
            <div className="divide-y divide-slate-100" data-testid="landing-feature-comparison-grid">
              {[
                { f_hi:'नोटिस सरल करें', f_en:'Notice Simplifier', cat:'admin', tier:1 },
                { f_hi:'फीस याद दिलाना', f_en:'Fee Reminder AI', cat:'admin', tier:1 },
                { f_hi:'संदेश टेम्पलेट', f_en:'Message Templates', cat:'admin', tier:1 },
                { f_hi:'Attendance Alert', f_en:'Attendance Alert', cat:'admin', tier:1 },
                { f_hi:'शिकायत का जवाब', f_en:'Complaint Reply', cat:'admin', tier:2 },
                { f_hi:'रिपोर्ट कार्ड AI', f_en:'AI Report Card', cat:'admin', tier:2 },
                { f_hi:'नियम चेकलिस्ट', f_en:'Compliance Checklist', cat:'admin', tier:2 },
                { f_hi:'MCQ Generator', f_en:'MCQ Generator', cat:'teacher', tier:2 },
                { f_hi:'प्रगति रिपोर्ट', f_en:'Progress Summary', cat:'teacher', tier:2 },
                { f_hi:'कमज़ोर टॉपिक्स', f_en:'Class Weak Topics', cat:'teacher', tier:3 },
                { f_hi:'At-Risk Student Alerts', f_en:'At-Risk Student Alerts', cat:'teacher', tier:3 },
                { f_hi:'प्रश्न पत्र बनाएं', f_en:'Question Paper Maker', cat:'teacher', tier:3 },
                { f_hi:'Homework Hints', f_en:'Homework Hints', cat:'student', tier:1 },
                { f_hi:'Chapter Explainer', f_en:'Chapter Explainer', cat:'student', tier:2 },
                { f_hi:'Study Plan', f_en:'Study Plan', cat:'student', tier:2 },
                { f_hi:'Doubt Assistant', f_en:'Doubt Assistant', cat:'student', tier:3 },
                { f_hi:'Daily Summary', f_en:'Parent Daily Summary', cat:'parent', tier:1 },
                { f_hi:'Caption Writer', f_en:'Caption Writer', cat:'admin', tier:1 },
                { f_hi:'Hashtag Maker', f_en:'Hashtag Maker', cat:'admin', tier:1 },
                { f_hi:'Welcome Video Script', f_en:'Welcome Video Script', cat:'admin', tier:2 },
              ].map((row, idx) => {
                const tierStyles = {
                  1: { bg:'bg-green-100', text:'text-green-700', label:'Flash-Lite', cost:'₹0.05/call' },
                  2: { bg:'bg-blue-100', text:'text-blue-700', label:'Flash', cost:'₹0.12/call' },
                  3: { bg:'bg-purple-100', text:'text-purple-700', label:'Pro', cost:'₹0.40/call' },
                };
                const catMap = { admin:{emoji:'🏫',hi:'Admin',en:'Admin',c:'text-orange-600'}, teacher:{emoji:'👨‍🏫',hi:'Teacher',en:'Teacher',c:'text-blue-600'}, student:{emoji:'👨‍🎓',hi:'Student',en:'Student',c:'text-green-600'}, parent:{emoji:'👨‍👩‍👧',hi:'Parent',en:'Parent',c:'text-purple-600'} };
                const t = tierStyles[row.tier];
                const cat = catMap[row.cat];
                return (
                  <div key={idx} className="grid gap-3 px-5 py-4 md:grid-cols-[2.2fr_1fr_1fr_0.9fr] md:items-center hover:bg-slate-50 transition-colors" data-testid={`landing-feature-comparison-row-${idx}`}>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 md:hidden">{language === 'hi' ? 'Feature' : 'Feature'}</p>
                      <p className="text-sm font-medium text-slate-800">{language === 'hi' ? row.f_hi : row.f_en}</p>
                    </div>
                    <div className="md:text-center">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 md:hidden">{language === 'hi' ? 'Category' : 'Category'}</p>
                      <span className={`text-xs font-medium ${cat.c}`}>{cat.emoji} {language === 'hi' ? cat.hi : cat.en}</span>
                    </div>
                    <div className="md:text-center">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 md:hidden">{language === 'hi' ? 'AI Model' : 'AI Model'}</p>
                      <span className={`inline-block px-3 py-1 ${t.bg} ${t.text} text-xs font-semibold rounded-full`}>
                        T{row.tier} · {t.label}
                      </span>
                    </div>
                    <div className="md:text-center">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 md:hidden">{language === 'hi' ? 'Cost' : 'Cost'}</p>
                      <span className="text-xs text-slate-500">{t.cost}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-4 bg-gradient-to-r from-green-50 via-blue-50 to-purple-50 border-t border-slate-200 text-center">
              <p className="text-sm text-slate-700 font-medium">
                {language === 'hi' 
                  ? '💡 सभी AI calls आपकी खुद की Gemini API Key से होते हैं — Platform को कोई charge नहीं।' 
                  : '💡 All AI calls run on your own Gemini API Key — zero cost to the platform.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 100% FREE Platform banner (replaces Founding Offer) */}
      <section className="pt-14 pb-4 px-4 md:px-8 relative z-10" data-testid="free-platform-offer">
        <div className="max-w-5xl mx-auto">
          <div className="relative bg-gradient-to-r from-emerald-600 via-green-500 to-teal-600 rounded-3xl p-8 md:p-10 shadow-2xl overflow-hidden text-center">
            <div className="absolute -top-20 -right-10 w-64 h-64 bg-yellow-300/20 rounded-full blur-3xl pointer-events-none"></div>
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white text-emerald-700 rounded-full text-xs font-black uppercase tracking-wider mb-3 shadow-lg">
                <i className="fa-solid fa-gift"></i> {language === 'hi' ? '100% FREE Platform' : '100% FREE Platform'}
              </div>
              <h3 className="text-2xl md:text-4xl font-black text-white mb-2 tracking-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
                {language === 'hi' ? '🎉 Software हमेशा के लिए FREE' : '🎉 Software Free Forever'}
              </h3>
              <p className="text-white/90 text-base md:text-lg mb-4 max-w-2xl mx-auto" style={{fontFamily: 'Manrope, sans-serif'}}>
                {language === 'hi'
                  ? 'सिर्फ़ one-time license/setup + सालाना maintenance। कोई per-user charge नहीं — plan आपके कुल users (छात्र+शिक्षक+staff) पर आधारित।'
                  : 'Only a one-time license/setup + yearly maintenance. No per-user charge — plan based on your total users (students+teachers+staff).'}
              </p>
              <button onClick={() => setShowDemoForm(true)} className="px-6 py-3 bg-white text-emerald-700 font-bold rounded-xl hover:bg-emerald-50 transition shadow-lg" data-testid="free-platform-cta">
                {language === 'hi' ? 'अभी शुरू करें →' : 'Get Started →'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Founding Schools Launch Offer — hidden (offer disabled; code kept) */}
      {foundingStats.is_open && (
      <section className="pt-14 pb-4 px-4 md:px-8 relative z-10" data-testid="founding-schools-offer">
        <div className="max-w-5xl mx-auto">
          <div className="relative bg-gradient-to-r from-orange-600 via-red-500 to-pink-600 rounded-3xl p-8 md:p-10 shadow-2xl overflow-hidden">
            {/* Decorative orbs */}
            <div className="absolute -top-20 -right-10 w-64 h-64 bg-yellow-300/20 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute -bottom-20 -left-10 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none"></div>

            <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
              {/* Left: Badge + Content */}
              <div className="flex-1 text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-yellow-400 text-slate-900 rounded-full text-xs font-black uppercase tracking-wider mb-3 shadow-lg">
                  <i className="fa-solid fa-bolt"></i>
                  {language === 'hi' ? 'Limited Early Access' : 'Limited Early Access'}
                </div>
                <h3 className="text-2xl md:text-4xl font-black text-white mb-2 tracking-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
                  {language === 'hi' 
                    ? '🚀 Founding Schools Program' 
                    : '🚀 Founding Schools Program'}
                </h3>
                <p className="text-white/90 text-base md:text-lg mb-3" style={{fontFamily: 'Manrope, sans-serif'}}>
                  {language === 'hi' 
                    ? `पहले ${FOUNDING_LIMIT} स्कूल जो payment करें — उन्हें one-time पर ${FOUNDING_PCT}% OFF, सिर्फ ${CURR}${FOUNDING_ONE} (+ ${CURR}${MAINT}/साल maintenance)। Seat सिर्फ payment पर reserve होती है (trial के दौरान नहीं)।`
                    : `First ${FOUNDING_LIMIT} schools that pay get ${FOUNDING_PCT}% OFF the one-time — just ${CURR}${FOUNDING_ONE} (+ ${CURR}${MAINT}/year maintenance). Seat is reserved only on payment (not during trial).`}
                </p>
                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-semibold rounded-full border border-white/30">
                    <i className="fa-solid fa-percent"></i> {language === 'hi' ? `payment पर ${FOUNDING_PCT}% off` : `${FOUNDING_PCT}% off on payment`}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-semibold rounded-full border border-white/30">
                    <i className="fa-solid fa-star"></i> {language === 'hi' ? 'Priority Onboarding' : 'Priority Onboarding'}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-semibold rounded-full border border-white/30">
                    <i className="fa-solid fa-medal"></i> {language === 'hi' ? 'Founding Member Badge' : 'Founding Member Badge'}
                  </span>
                </div>
              </div>

              {/* Right: CTA + Counter */}
              <div className="flex-shrink-0 text-center">
                <div className="bg-white rounded-2xl p-5 shadow-xl">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                    {language === 'hi' ? 'उपलब्ध Seats' : 'Seats Left'}
                  </p>
                  <p className="text-5xl font-black text-orange-600 mb-1" style={{fontFamily: 'Outfit, sans-serif'}}>
                    <span data-testid="founding-seats-left">{foundingStats.remaining}</span>
                    <span className="text-xl text-slate-400">/{foundingStats.total_slots}</span>
                  </p>
                  <div className="w-full bg-slate-200 rounded-full h-2 mb-3">
                    <div className="bg-gradient-to-r from-orange-500 to-red-500 h-2 rounded-full transition-all duration-500" style={{width: `${foundingStats.percent_filled}%`}}></div>
                  </div>
                  {foundingStats.is_open && foundingStats.remaining <= 10 && (
                    <p className="text-xs font-bold text-red-600 mb-2 animate-pulse" data-testid="founding-urgency">
                      {language === 'hi'
                        ? `🔥 सिर्फ़ ${foundingStats.remaining} seats बची हैं — अभी subscribe करें!`
                        : `🔥 Only ${foundingStats.remaining} seats left — subscribe now!`}
                    </p>
                  )}
                  <button
                    onClick={() => setShowDemoForm(true)}
                    disabled={!foundingStats.is_open}
                    className="w-full px-6 py-3 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="founding-schools-cta"
                  >
                    {!foundingStats.is_open
                      ? (language === 'hi' ? 'Program Full हो गया' : 'Program Full')
                      : (language === 'hi' ? 'अभी Apply करें →' : 'Apply Now →')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Happy Customers — Testimonials (Auto-Scrolling Carousel, Right-to-Left) */}
      <section id="testimonials" className="py-20 px-4 md:px-8 relative z-10 overflow-hidden" data-testid="testimonials-section">
        <style>{`
          @keyframes slideLeft {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .testimonials-track {
            animation: slideLeft 50s linear infinite;
          }
          .testimonials-container:hover .testimonials-track {
            animation-play-state: paused;
          }
        `}</style>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-2 bg-amber-100 text-amber-600 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider" style={{fontFamily: 'Manrope, sans-serif'}}>
              ⭐ {language === 'hi' ? 'Reviews' : 'Reviews'}
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>
              {t.testimonials.title}
            </h2>
            <p className="text-slate-500 text-lg" style={{fontFamily: 'Manrope, sans-serif'}}>
              {language === 'hi' ? 'जो शिक्षक और Principals Shiksha Mitra से जुड़ चुके हैं' : 'Educators and Principals already on Shiksha Mitra'}
            </p>
          </div>

          {/* Auto-Scrolling Carousel */}
          <div className="testimonials-container relative">
            {/* Gradient Fade Edges */}
            <div className="absolute top-0 left-0 bottom-0 w-20 md:w-40 bg-gradient-to-r from-slate-50 to-transparent z-10 pointer-events-none"></div>
            <div className="absolute top-0 right-0 bottom-0 w-20 md:w-40 bg-gradient-to-l from-slate-50 to-transparent z-10 pointer-events-none"></div>

            <div className="testimonials-track flex gap-6 w-max">
              {/* Duplicate the list twice for seamless infinite loop */}
              {[...t.testimonials.items, ...t.testimonials.items].map((testimonial, idx) => (
                <div
                  key={idx}
                  className="w-[340px] md:w-[380px] flex-shrink-0 bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-xl hover:border-orange-200 transition-all duration-300"
                  data-testid={`testimonial-card-${idx}`}
                >
                  {/* Rating */}
                  <div className="flex gap-0.5 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className={`w-5 h-5 ${i < testimonial.rating ? 'text-yellow-400' : 'text-slate-200'}`} fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.539 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                    ))}
                  </div>

                  {/* Quote */}
                  <div className="mb-5 min-h-[120px]">
                    <span className="text-5xl text-orange-300 leading-none font-serif">"</span>
                    <p className="text-slate-700 text-base -mt-4 leading-relaxed" style={{fontFamily: 'Manrope, sans-serif'}}>
                      {testimonial.quote}
                    </p>
                  </div>

                  {/* Author */}
                  <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md flex-shrink-0">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-slate-900 truncate" style={{fontFamily: 'Outfit, sans-serif'}}>{testimonial.name}</p>
                      <p className="text-xs text-slate-500 truncate">{testimonial.role} · {testimonial.school}</p>
                      <p className="text-xs text-slate-400 truncate"><i className="fa-solid fa-location-dot mr-1"></i>{testimonial.location}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Platform Strengths Row (Factual, no fake stats) */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="platform-strengths">
            {[
              { icon: 'fa-robot', value: '31', label: language === 'hi' ? 'AI Features' : 'AI Features', color: 'text-orange-600', bg: 'bg-orange-50' },
              { icon: 'fa-cubes', value: '8', label: language === 'hi' ? 'ERP Modules' : 'ERP Modules', color: 'text-blue-600', bg: 'bg-blue-50' },
              { icon: 'fa-shield-halved', value: 'UDISE+', label: language === 'hi' ? 'Compliant' : 'Compliant', color: 'text-green-600', bg: 'bg-green-50' },
              { icon: 'fa-language', value: '2', label: language === 'hi' ? 'भाषाएँ (Hindi + English)' : 'Languages (Hindi + English)', color: 'text-purple-600', bg: 'bg-purple-50' },
            ].map((stat, idx) => (
              <div key={idx} className={`${stat.bg} border border-slate-200 rounded-2xl p-5 text-center hover:scale-105 transition-transform duration-300`}>
                <i className={`fa-solid ${stat.icon} ${stat.color} text-2xl mb-2`}></i>
                <div className={`text-2xl font-black ${stat.color}`} style={{fontFamily: 'Outfit, sans-serif'}}>{stat.value}</div>
                <div className="text-xs text-slate-500 mt-1" style={{fontFamily: 'Manrope, sans-serif'}}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Trust & Tech Badges */}
          <div className="mt-6 flex flex-wrap justify-center gap-3" data-testid="trust-badges">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 shadow-sm">
              <span className="text-base">🇮🇳</span> {language === 'hi' ? 'Made in India' : 'Made in India'}
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 shadow-sm">
              <i className="fa-solid fa-lock text-green-600"></i> {language === 'hi' ? 'Bank-grade Security' : 'Bank-grade Security'}
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 shadow-sm">
              <i className="fa-solid fa-circle-check text-blue-600"></i> {language === 'hi' ? 'Meta + Razorpay Verified' : 'Meta + Razorpay Verified'}
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 shadow-sm">
              <i className="fa-solid fa-user-shield text-purple-600"></i> {language === 'hi' ? 'GDPR-ready' : 'GDPR-ready'}
            </span>
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => setShowDemoForm(true)}
              className="px-8 py-4 bg-slate-900 text-white rounded-full hover:bg-slate-800 transition font-semibold text-lg shadow-xl"
              data-testid="testimonials-cta-btn"
            >
              {language === 'hi' ? '🚀 अपने स्कूल को भी डिजिटल बनाएं' : '🚀 Digitize Your School Too'}
            </button>
          </div>
        </div>
      </section>

      {/* Home About / Vision / Mission / Team */}
      <section id="about-home" className="py-24 px-4 md:px-8 relative z-10" data-testid="home-about-section">
        <div className="max-w-7xl mx-auto space-y-10">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider">
              {language === 'hi' ? 'About Shiksha Mitra' : 'About Shiksha Mitra'}
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>
              {language === 'hi' ? 'एक ऐसा platform जो school operations और school growth दोनों संभाले' : 'One platform built for school operations and school growth'}
            </h2>
            <p className="text-lg text-slate-500" style={{fontFamily: 'Manrope, sans-serif'}}>
              {language === 'hi'
                ? 'शिक्षा मित्र sirf software नहीं, बल्कि schools ke liye एक AI-first operating layer है — jo admission, ERP, communication, payment aur onboarding ko एक जगह लाती है।'
                : 'Shiksha Mitra is not just software, but an AI-first operating layer that brings admissions, ERP, communication, payments, and onboarding together.'}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {resolvedAboutCards.map((card, idx) => (
              <div key={idx} className="rounded-3xl bg-white p-8 shadow-sm border border-slate-200 hover:shadow-xl transition-all" data-testid={`home-about-card-${idx}`}>
                <div className="text-4xl mb-4">{card.icon}</div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>{card.title}</h3>
                <p className="text-slate-500 leading-relaxed" style={{fontFamily: 'Manrope, sans-serif'}}>{card.text}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-[2rem] bg-slate-950 text-white p-8 md:p-10 shadow-2xl" data-testid="home-vision-block">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/60">{language === 'hi' ? 'Vision' : 'Vision'}</p>
              <h3 className="mt-3 text-3xl font-black" style={{fontFamily: 'Outfit, sans-serif'}}>
                {resolvedVisionTitle}
              </h3>
              <p className="mt-4 text-white/75 leading-relaxed">{resolvedVisionBody}</p>
            </div>
            <div className="rounded-[2rem] bg-gradient-to-br from-orange-500 to-amber-500 text-white p-8 md:p-10 shadow-2xl" data-testid="home-mission-block">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/70">{language === 'hi' ? 'Mission' : 'Mission'}</p>
              <h3 className="mt-3 text-3xl font-black" style={{fontFamily: 'Outfit, sans-serif'}}>
                {resolvedMissionTitle}
              </h3>
              <p className="mt-4 text-white/85 leading-relaxed">{resolvedMissionBody}</p>
            </div>
          </div>

          <div className="rounded-3xl bg-white border border-slate-200 p-8 md:p-10 shadow-sm" data-testid="home-team-section">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-8">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">{language === 'hi' ? 'Team' : 'Team'}</p>
                <h3 className="mt-2 text-3xl font-black text-slate-900" style={{fontFamily: 'Outfit, sans-serif'}}>
                  {language === 'hi' ? 'Core team structure — real team details के लिए ready' : 'Core team structure — ready for real team details'}
                </h3>
              </div>
              <Link to="/about" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-900 text-white font-semibold hover:bg-slate-800 transition" data-testid="home-about-page-link">
                {language === 'hi' ? 'पूरा About Page देखें' : 'View Full About Page'}
              </Link>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
              {resolvedTeamMembers.map((member, idx) => (
                <div key={idx} className="rounded-2xl border border-dashed border-slate-300 p-5 bg-slate-50" data-testid={`home-team-card-${idx}`}>
                  <div className="w-14 h-14 rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-2xl mb-4 overflow-hidden">{member.image_url ? <img src={member.image_url} alt={member.role} className="w-full h-full object-cover" /> : '👤'}</div>
                  <h4 className="font-bold text-slate-900 text-lg">{member.role}</h4>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">{member.summary}</p>
                  <p className="mt-4 text-xs font-medium text-orange-600 uppercase tracking-wider">{language === 'hi' ? 'Profile details later' : 'Profile details later'}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Feature Detail Modal - Enhanced */}
      {selectedFeature && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 md:p-4" onClick={closeFeatureModal}>
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Back Button - Top */}
            <div className="bg-gray-100 px-4 py-2 flex items-center justify-between border-b">
              <button 
                onClick={closeFeatureModal}
                className="flex items-center gap-2 text-gray-600 hover:text-orange-500 transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>{language === 'hi' ? 'वापस जाएं' : 'Go Back'}</span>
              </button>
              <span className="text-sm text-gray-500">Feature {selectedFeature.day} / 31</span>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto max-h-[calc(95vh-120px)]">
              {/* Hero Section with Image */}
              <div className="relative">
                {featureDetail?.hero_image ? (
                  <img 
                    src={featureDetail.hero_image} 
                    alt={selectedFeature.feature}
                    className="w-full h-64 md:h-80 lg:h-96 object-cover"
                  />
                ) : (
                  <div className={`w-full h-64 md:h-80 lg:h-96 ${
                    selectedFeature.category === 'admin' ? 'bg-gradient-to-r from-orange-400 to-amber-400' :
                    selectedFeature.category === 'teacher' ? 'bg-gradient-to-r from-blue-400 to-cyan-400' :
                    selectedFeature.category === 'student' ? 'bg-gradient-to-r from-green-400 to-emerald-400' :
                    'bg-gradient-to-r from-purple-400 to-pink-400'
                  }`} />
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-2 ${
                    selectedFeature.category === 'admin' ? 'bg-orange-500 text-white' :
                    selectedFeature.category === 'teacher' ? 'bg-blue-500 text-white' :
                    selectedFeature.category === 'student' ? 'bg-green-500 text-white' :
                    'bg-purple-500 text-white'
                  }`}>
                    AI Feature #{selectedFeature.day}
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold text-white">{selectedFeature.feature}</h2>
                  <p className="text-white/80 text-sm mt-1">
                    {language === 'hi' ? selectedFeature.title_hi : selectedFeature.title_en}
                  </p>
                </div>
              </div>

              {featureDetailLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
                </div>
              ) : featureDetail ? (
                <div className="p-4 md:p-6 space-y-6">
                  {/* Section 1: Problems */}
                  <div className="bg-red-50 rounded-xl p-5 border border-red-100">
                    <h3 className="text-lg font-bold text-red-700 mb-3 flex items-center gap-2">
                      <span>😟</span>
                      {language === 'hi' ? featureDetail.problems?.title_hi : featureDetail.problems?.title_en}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      {language === 'hi' ? featureDetail.problems?.description_hi : featureDetail.problems?.description_en}
                    </p>
                    
                    {/* School Examples */}
                    <div className="space-y-3">
                      {featureDetail.problems?.examples?.map((example, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-4 border border-red-100">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-red-500 font-bold">{idx + 1}</span>
                            </div>
                            <div>
                              <p className="font-semibold text-gray-800 text-sm">{example.school}</p>
                              <p className="text-gray-600 text-sm mt-1 italic">
                                "{language === 'hi' ? example.problem_hi : example.problem_en}"
                              </p>
                              <p className="text-gray-400 text-xs mt-1">— {example.person}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Section 2: Current Solutions & Challenges */}
                  <div className="bg-yellow-50 rounded-xl p-5 border border-yellow-100">
                    <h3 className="text-lg font-bold text-yellow-700 mb-3 flex items-center gap-2">
                      <span>🤔</span>
                      {language === 'hi' ? featureDetail.current_solutions?.title_hi : featureDetail.current_solutions?.title_en}
                    </h3>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">{language === 'hi' ? 'वर्तमान तरीके:' : 'Current Methods:'}</p>
                        <ul className="space-y-1">
                          {(language === 'hi' ? featureDetail.current_solutions?.methods_hi : featureDetail.current_solutions?.methods_en)?.map((method, idx) => (
                            <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                              <span className="text-yellow-500">•</span>
                              {method}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">{language === 'hi' ? 'चुनौतियाँ:' : 'Challenges:'}</p>
                        <ul className="space-y-1">
                          {(language === 'hi' ? featureDetail.current_solutions?.challenges_hi : featureDetail.current_solutions?.challenges_en)?.map((challenge, idx) => (
                            <li key={idx} className="text-sm text-red-600">{challenge}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Our Solution */}
                  <div className="bg-green-50 rounded-xl p-5 border border-green-100">
                    <h3 className="text-lg font-bold text-green-700 mb-3 flex items-center gap-2">
                      <span>✨</span>
                      {language === 'hi' ? featureDetail.solution?.title_hi : featureDetail.solution?.title_en}
                    </h3>
                    <ul className="grid md:grid-cols-2 gap-2">
                      {(language === 'hi' ? featureDetail.solution?.features_hi : featureDetail.solution?.features_en)?.map((feat, idx) => (
                        <li key={idx} className="text-sm text-gray-700">{feat}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Section 4: Benefits */}
                  <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
                    <h3 className="text-lg font-bold text-blue-700 mb-3 flex items-center gap-2">
                      <span>🎯</span>
                      {language === 'hi' ? featureDetail.benefits?.title_hi : featureDetail.benefits?.title_en}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {(language === 'hi' ? featureDetail.benefits?.items_hi : featureDetail.benefits?.items_en)?.map((benefit, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-3 text-center border border-blue-100">
                          <span className="text-2xl">{benefit.icon}</span>
                          <p className="text-xs text-gray-600 mt-1">{benefit.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Testimonial */}
                  {featureDetail.testimonial && (
                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-100">
                      <div className="flex items-start gap-3">
                        <span className="text-4xl text-orange-400">"</span>
                        <div>
                          <p className="text-gray-700 italic">
                            {language === 'hi' ? featureDetail.testimonial.quote_hi : featureDetail.testimonial.quote_en}
                          </p>
                          <p className="text-gray-500 text-sm mt-2">
                            — <strong>{featureDetail.testimonial.name}</strong>, {featureDetail.testimonial.role}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-6">
                  <div className="bg-gray-50 rounded-xl p-5 whitespace-pre-wrap text-gray-700 text-sm leading-relaxed">
                    {language === 'hi' ? selectedFeature.message_hi : selectedFeature.message_en}
                  </div>
                </div>
              )}
            </div>

            {/* Fixed Footer with Single CTA */}
            <div className="border-t bg-white p-4">
              {/* Main CTA - Ask Shiksha Mitra */}
              <button 
                onClick={() => { 
                  closeFeatureModal(); 
                  setFeatureContextForChat({ 
                    day: selectedFeature.day, 
                    feature: selectedFeature.feature,
                    category: selectedFeature.category
                  });
                }}
                className="w-full px-6 py-4 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 text-white rounded-xl hover:from-orange-600 hover:via-amber-600 hover:to-orange-600 transition font-bold text-lg flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:scale-[1.02] animate-pulse hover:animate-none"
                data-testid="ask-shiksha-mitra-btn"
              >
                <span className="text-2xl">💬</span>
                <span>{language === 'hi' ? 'शिक्षा मित्र से पूछें' : 'Ask Shiksha Mitra'}</span>
                <span className="text-xs bg-white/20 px-2 py-1 rounded-full">आयुष Online</span>
              </button>
              
              <p className="text-center text-gray-500 text-xs mt-2">
                {language === 'hi' 
                  ? '💡 Demo बुक करें • Pricing जानें • सवाल पूछें - सब कुछ यहीं से!' 
                  : '💡 Book Demo • Know Pricing • Ask Questions - Everything from here!'}
              </p>
              
              {/* Navigation */}
              <div className="flex justify-between mt-3 pt-3 border-t">
                <button 
                  onClick={() => navigateFeature('prev')}
                  className="px-4 py-2 text-gray-600 hover:text-orange-500 transition flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  {language === 'hi' ? 'पिछला Feature' : 'Previous Feature'}
                </button>
                <button 
                  onClick={() => navigateFeature('next')}
                  className="px-4 py-2 text-gray-600 hover:text-orange-500 transition flex items-center gap-2"
                >
                  {language === 'hi' ? 'अगला Feature' : 'Next Feature'}
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Section — Slab/Tier Calculator */}
      <section id="pricing" className="py-20 bg-gray-50 relative z-10">
        <div className="max-w-xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>
              {language === 'hi' ? 'Software Lifetime FREE' : 'Software Lifetime FREE'}
            </h2>
            <p className="text-gray-500 text-base">
              {language === 'hi' ? '100% FREE Platform — plan आपके कुल users (छात्र+शिक्षक+staff) पर आधारित' : '100% FREE Platform — plan based on your total users (students+teachers+staff)'}
            </p>
          </div>

          {/* Calculator Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden" data-testid="setup-calculator">
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-4 text-white text-center">
              <h3 className="text-lg font-bold">{language === 'hi' ? 'Pricing Calculator' : 'Pricing Calculator'}</h3>
              <p className="text-sm text-white/80">{language === 'hi' ? 'One-time + सालाना maintenance' : 'One-time + yearly maintenance'}</p>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <span className="text-xl w-8 text-center">👨‍👩‍👧</span>
                <div className="flex-1">
                  <label className="text-xs font-medium text-gray-500 block mb-1">{language === 'hi' ? 'आपके कुल users (छात्र+शिक्षक+staff)' : 'Total users (students+teachers+staff)'}</label>
                  <input
                    type="number"
                    min="0"
                    value={calcStudents}
                    onChange={(e) => setCalcStudents(e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder={language === 'hi' ? 'जैसे: 250' : 'e.g. 250'}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    data-testid="calc-students"
                  />
                </div>
              </div>

              {/* Result Box */}
              {calcCustom ? (
                <div className="bg-slate-900 text-white rounded-xl p-5 mt-2" data-testid="calc-custom-box">
                  <p className="font-semibold text-amber-300 mb-1">{language === 'hi' ? '🏛️ बड़े स्कूल (5000+ छात्र)' : '🏛️ Large school (5000+ students)'}</p>
                  <p className="text-sm text-gray-300 mb-3">{language === 'hi' ? 'आपके स्कूल के लिए custom pricing है। हमारी sales team सबसे अच्छा plan देगी।' : 'Custom pricing for your school. Our sales team will get you the best plan.'}</p>
                  <a
                    href={`https://wa.me/91${SALES_PHONE}?text=${encodeURIComponent(language === 'hi' ? `नमस्ते! हमारे स्कूल में ${calcCount} छात्र हैं। कृपया Shiksha Mitra custom pricing बताएँ।` : `Hi! Our school has ${calcCount} students. Please share Shiksha Mitra custom pricing.`)}`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg font-bold text-white transition"
                    data-testid="calc-contact-sales"
                  >
                    <i className="fa-brands fa-whatsapp"></i> {language === 'hi' ? 'Sales से संपर्क करें' : 'Contact Sales'}
                  </a>
                </div>
              ) : (
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-5 mt-2 border border-orange-200">
                  {calcTier && (
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">{language === 'hi' ? 'आपका plan' : 'Your plan'}</span>
                      <span className="text-sm font-bold text-gray-800" data-testid="calc-tier-label">{language === 'hi' ? calcTier.label_hi : calcTier.label_en}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-600">{language === 'hi' ? 'One-time (license + setup)' : 'One-time (license + setup)'}</span>
                    <span className="text-sm font-semibold text-gray-800" data-testid="calc-one-time">{calcCount > 0 ? `${CURR}${calcOneTime.toLocaleString('en-IN')}` : '—'}</span>
                  </div>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm text-gray-600">{language === 'hi' ? 'सालाना maintenance' : 'Annual maintenance'}</span>
                    <span className="text-sm font-semibold text-gray-800" data-testid="calc-maintenance">{calcCount > 0 ? `${CURR}${calcMaint.toLocaleString('en-IN')}${language === 'hi' ? '/साल' : '/yr'}` : '—'}</span>
                  </div>
                  <div className="border-t border-orange-200 pt-3 flex justify-between items-center">
                    <span className="font-bold text-gray-800">{language === 'hi' ? 'अभी देय (पहला भुगतान)' : 'Payable now (first payment)'}</span>
                    <span className="text-3xl font-black text-orange-600" data-testid="calc-total-cost">{calcCount > 0 ? `${CURR}${calcTotal.toLocaleString('en-IN')}` : `${CURR}0`}</span>
                  </div>
                </div>
              )}

              <p className="text-xs text-center text-gray-400 px-4">
                {language === 'hi' ? 'Software 100% FREE। AI FREE (BYOK)। कोई per-user charge नहीं — पहला भुगतान = one-time + पहले साल का maintenance।' : 'Software 100% FREE. AI FREE (BYOK). No per-user charge — first payment = one-time + first-year maintenance.'}
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                {!calcCustom && calcCount >= 1 && (
                  <button onClick={() => { setPaymentModalPrefill({ studentCount: calcCount, source: 'landing_calculator_pay' }); setShowPaymentModal(true); }}
                    className="flex-1 py-3.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-bold text-lg hover:from-green-600 hover:to-emerald-700 transition shadow-lg"
                    data-testid="calc-pay-now">
                    {language === 'hi' ? 'अभी भुगतान करें' : 'Pay Now'} →
                  </button>
                )}
                <button onClick={() => setShowDemoForm(true)}
                  className="flex-1 py-3.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-bold text-lg hover:from-orange-600 hover:to-amber-600 transition shadow-lg"
                  data-testid="calc-get-started">
                  {language === 'hi' ? 'Free Demo Book करें' : 'Book Free Demo'} →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Pricing Calculator Popup Modal */}
      <PricingCalculatorModal
        isOpen={showPricingCalculator}
        onClose={() => setShowPricingCalculator(false)}
        t={t}
        language={language}
        onOpenDemo={() => setShowDemoForm(true)}
      />

      {/* Live Chat Widget */}
      <LiveChat 
        initialFeatureContext={featureContextForChat}
        onChatOpen={() => setFeatureContextForChat(null)}
        onOpenDemo={(prefill) => {
          setDemoModalPrefill(prefill || null);
          setShowDemoForm(true);
        }}
        onOpenPayment={(prefill) => {
          if (prefill?.suggestedPlan) setSelectedPlanForPayment(prefill.suggestedPlan);
          if (prefill?.suggestedSize) setSelectedSize(prefill.suggestedSize);
          setPaymentModalPrefill(prefill || null);
          setPaymentModalStartStep(prefill?.startStep || 2);
          setShowPaymentModal(true);
        }}
      />

      <DemoFormModal
        isOpen={showDemoForm}
        onClose={() => {
          setShowDemoForm(false);
          setDemoModalPrefill(null);
        }}
        t={t}
        language={language}
        prefillData={demoModalPrefill}
        source={demoModalPrefill?.source || 'landing_page_demo_form'}
      />

      {/* Footer — 5-Column SaaS Style */}
      <footer className="bg-slate-950 text-white pt-16 pb-6 px-4 relative z-10" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-6 lg:grid-cols-12 gap-8 mb-10">
            {/* Column 1: Brand + Newsletter (spans 3 cols on lg) */}
            <div className="col-span-2 md:col-span-6 lg:col-span-3">
              <div className="flex items-center gap-2 mb-4">
                <img src="https://customer-assets.emergentagent.com/job_b71b88a0-de31-4e82-b047-c99df2477168/artifacts/7dlw52ts_logo%20%281%29.jpg" alt="Logo" className="h-12 w-12 rounded-xl" />
                <span className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-green-400 bg-clip-text text-transparent" style={{fontFamily: 'Outfit, sans-serif'}}>शिक्षा मित्र</span>
              </div>
              <p className="text-slate-400 text-sm mb-5 leading-relaxed">
                {language === 'hi' 
                  ? 'भारत का #1 AI-Powered School Management Platform। 31 AI Features + 8 ERP Modules, सब कुछ FREE।' 
                  : "India's #1 AI-Powered School Management Platform. 31 AI Features + 8 ERP Modules, all FREE."}
              </p>

              {/* Newsletter Signup */}
              <div className="mb-5">
                <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                  <i className="fa-solid fa-envelope-open-text text-orange-400"></i>
                  {language === 'hi' ? 'Newsletter — Updates पाएं' : 'Newsletter — Get Updates'}
                </h4>
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const email = e.target.elements.newsletterEmail.value;
                    if (!email) return;
                    try {
                      await fetch(`${API_URL}/api/leads/demo-request`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email, source: 'newsletter_footer', name: 'Newsletter Subscriber', language })
                      });
                      e.target.reset();
                      e.target.elements.newsletterEmail.placeholder = language === 'hi' ? '✓ धन्यवाद! Subscribed' : '✓ Thanks! Subscribed';
                    } catch (err) { console.error(err); }
                  }}
                  className="flex gap-2"
                  data-testid="newsletter-form"
                >
                  <input
                    name="newsletterEmail"
                    type="email"
                    required
                    placeholder={language === 'hi' ? 'आपका email' : 'Your email'}
                    className="flex-1 min-w-0 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-orange-500"
                    data-testid="newsletter-email-input"
                  />
                  <button
                    type="submit"
                    className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg text-sm font-semibold transition whitespace-nowrap"
                    data-testid="newsletter-submit-btn"
                  >
                    {language === 'hi' ? 'भेजें' : 'Subscribe'}
                  </button>
                </form>
              </div>

              {/* Address */}
              <div className="text-xs text-slate-500 leading-relaxed flex items-start gap-2">
                <i className="fa-solid fa-location-dot mt-0.5 text-orange-400"></i>
                <span>System Of Success, India</span>
              </div>
            </div>

            {/* Column 2: Product */}
            <div className="col-span-1 md:col-span-2">
              <h4 className="font-semibold mb-4 text-white flex items-center gap-2">
                <i className="fa-solid fa-box-open text-orange-400"></i>
                {language === 'hi' ? 'Product' : 'Product'}
              </h4>
              <ul className="space-y-2.5 text-sm text-slate-400">
                <li><a href="#features" className="hover:text-orange-400 transition flex items-center gap-2"><i className="fa-solid fa-bullseye w-4"></i>{language === 'hi' ? 'Features' : 'Features'}</a></li>
                <li><button onClick={() => setShowPricingCalculator(true)} className="hover:text-orange-400 transition flex items-center gap-2 text-left"><i className="fa-solid fa-calculator w-4"></i>{language === 'hi' ? 'Pricing Calculator' : 'Pricing Calculator'}</button></li>
                <li><Link to="/admission" className="hover:text-orange-400 transition flex items-center gap-2"><i className="fa-solid fa-rocket w-4"></i>{language === 'hi' ? 'Admission Engine' : 'Admission Engine'}</Link></li>
                <li><Link to="/features" className="hover:text-orange-400 transition flex items-center gap-2"><i className="fa-solid fa-bullhorn w-4"></i>{language === 'hi' ? 'Campaign Manager' : 'Campaign Manager'}</Link></li>
                <li><button onClick={() => setShowDemoForm(true)} className="hover:text-orange-400 transition flex items-center gap-2 text-left"><i className="fa-solid fa-calendar-check w-4"></i>{language === 'hi' ? 'Book Demo' : 'Book Demo'}</button></li>
                <li><button onClick={onLoginClick} className="hover:text-orange-400 transition flex items-center gap-2 text-left"><i className="fa-solid fa-right-to-bracket w-4"></i>{language === 'hi' ? 'Login' : 'Login'}</button></li>
              </ul>
            </div>

            {/* Column 3: Solutions */}
            <div className="col-span-1 md:col-span-2">
              <h4 className="font-semibold mb-4 text-white flex items-center gap-2">
                <i className="fa-solid fa-users-gear text-orange-400"></i>
                {language === 'hi' ? 'Solutions' : 'Solutions'}
              </h4>
              <ul className="space-y-2.5 text-sm text-slate-400">
                <li><Link to="/features" className="hover:text-orange-400 transition flex items-center gap-2"><i className="fa-solid fa-user-tie w-4"></i>{language === 'hi' ? 'School Admin के लिए' : 'For School Admin'}</Link></li>
                <li><Link to="/features" className="hover:text-orange-400 transition flex items-center gap-2"><i className="fa-solid fa-chalkboard-user w-4"></i>{language === 'hi' ? 'Teachers के लिए' : 'For Teachers'}</Link></li>
                <li><Link to="/features" className="hover:text-orange-400 transition flex items-center gap-2"><i className="fa-solid fa-people-roof w-4"></i>{language === 'hi' ? 'Parents के लिए' : 'For Parents'}</Link></li>
                <li><Link to="/features" className="hover:text-orange-400 transition flex items-center gap-2"><i className="fa-solid fa-user-graduate w-4"></i>{language === 'hi' ? 'Students के लिए' : 'For Students'}</Link></li>
                <li><Link to="/school-match" className="hover:text-orange-400 transition flex items-center gap-2"><i className="fa-solid fa-magnifying-glass-location w-4"></i>{language === 'hi' ? 'School Finder' : 'School Finder'}</Link></li>
              </ul>
            </div>

            {/* Column 4: Company */}
            <div className="col-span-1 md:col-span-3 lg:col-span-2">
              <h4 className="font-semibold mb-4 text-white flex items-center gap-2">
                <i className="fa-solid fa-building text-orange-400"></i>
                {language === 'hi' ? 'Company' : 'Company'}
              </h4>
              <ul className="space-y-2.5 text-sm text-slate-400">
                <li><Link to="/about" className="hover:text-orange-400 transition flex items-center gap-2"><i className="fa-solid fa-circle-info w-4"></i>{language === 'hi' ? 'About Us' : 'About Us'}</Link></li>
                <li><Link to="/contact" className="hover:text-orange-400 transition flex items-center gap-2"><i className="fa-solid fa-headset w-4"></i>{language === 'hi' ? 'Contact Us' : 'Contact Us'}</Link></li>
                <li><a href="https://shiksha-mitra.com/blog" className="hover:text-orange-400 transition flex items-center gap-2"><i className="fa-solid fa-newspaper w-4"></i>Blog</a></li>
                <li><Link to="/contact" className="hover:text-orange-400 transition flex items-center gap-2"><i className="fa-solid fa-briefcase w-4"></i>{language === 'hi' ? 'Careers' : 'Careers'}</Link></li>
                <li><Link to="/contact" className="hover:text-orange-400 transition flex items-center gap-2"><i className="fa-solid fa-life-ring w-4"></i>{language === 'hi' ? 'Support' : 'Support'}</Link></li>
              </ul>
            </div>

            {/* Column 5: Legal + Contact + Social */}
            <div className="col-span-1 md:col-span-3 lg:col-span-3">
              <h4 className="font-semibold mb-4 text-white flex items-center gap-2">
                <i className="fa-solid fa-scale-balanced text-orange-400"></i>
                {language === 'hi' ? 'Legal & Contact' : 'Legal & Contact'}
              </h4>
              <ul className="space-y-2.5 text-sm text-slate-400 mb-5">
                <li><Link to="/privacy" className="hover:text-orange-400 transition flex items-center gap-2"><i className="fa-solid fa-user-shield w-4"></i>{language === 'hi' ? 'Privacy Policy' : 'Privacy Policy'}</Link></li>
                <li><Link to="/terms" className="hover:text-orange-400 transition flex items-center gap-2"><i className="fa-solid fa-file-contract w-4"></i>{language === 'hi' ? 'Terms & Conditions' : 'Terms & Conditions'}</Link></li>
                <li><Link to="/refund" className="hover:text-orange-400 transition flex items-center gap-2"><i className="fa-solid fa-rotate-left w-4"></i>{language === 'hi' ? 'Refund Policy' : 'Refund Policy'}</Link></li>
                <li><Link to="/data-sources" className="hover:text-orange-400 transition flex items-center gap-2" data-testid="footer-data-sources"><i className="fa-solid fa-shield-halved w-4"></i>{language === 'hi' ? 'Data Sources & पारदर्शिता' : 'Data Sources & Transparency'}</Link></li>
                <li className="flex items-center gap-2"><i className="fa-solid fa-phone text-green-400 w-4"></i><a href="tel:+919183100206" className="hover:text-orange-400 transition">+91 9183100206</a></li>
                <li className="flex items-center gap-2"><i className="fa-solid fa-envelope text-blue-400 w-4"></i><a href="mailto:admin@shiksha-mitra.com" className="hover:text-orange-400 transition">admin@shiksha-mitra.com</a></li>
              </ul>

              {/* Social Icons */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">{language === 'hi' ? 'हमसे जुड़ें' : 'Follow Us'}</p>
                <div className="flex flex-wrap gap-2">
                  <a href="https://www.instagram.com/ms_shiksha_mitra/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center hover:scale-110 transition-transform shadow-lg" data-testid="social-instagram" aria-label="Instagram">
                    <i className="fa-brands fa-instagram text-white text-lg"></i>
                  </a>
                  <a href="https://wa.me/919183100206" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center hover:scale-110 transition-transform shadow-lg" data-testid="social-whatsapp" aria-label="WhatsApp">
                    <i className="fa-brands fa-whatsapp text-white text-lg"></i>
                  </a>
                  <a href="https://www.facebook.com/shikshamitra" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center hover:scale-110 transition-transform shadow-lg" data-testid="social-facebook" aria-label="Facebook">
                    <i className="fa-brands fa-facebook-f text-white text-lg"></i>
                  </a>
                  <a href="https://www.youtube.com/@shikshamitra" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center hover:scale-110 transition-transform shadow-lg" data-testid="social-youtube" aria-label="YouTube">
                    <i className="fa-brands fa-youtube text-white text-lg"></i>
                  </a>
                  <a href="https://www.linkedin.com/company/shiksha-mitra" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-blue-700 flex items-center justify-center hover:scale-110 transition-transform shadow-lg" data-testid="social-linkedin" aria-label="LinkedIn">
                    <i className="fa-brands fa-linkedin-in text-white text-lg"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-slate-800 pt-6 flex flex-col md:flex-row justify-between items-center gap-3 text-sm text-slate-500">
            <p>{t.footer.copyright}</p>
            <p className="flex items-center gap-1.5">
              {language === 'hi' ? 'Made with' : 'Made with'} <i className="fa-solid fa-heart text-red-500"></i> {language === 'hi' ? 'in भारत 🇮🇳' : 'in India 🇮🇳'}
            </p>
          </div>
        </div>
      </footer>

      {/* Subscription Payment Modal */}
      <SubscriptionPaymentModal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setSelectedPlanForPayment(null);
          setPaymentModalPrefill(null);
          setPaymentModalStartStep(1);
        }}
        language={language}
        preSelectedPlan={selectedPlanForPayment}
        preSelectedSize={selectedSize}
        prefillData={paymentModalPrefill}
        startStep={paymentModalStartStep}
        source={paymentModalPrefill?.source || 'landing_page_payment'}
      />
    </div>
  );
}
